import type { FC } from 'react';

import type { CarePlanViewModel } from '@eluve/api-contract';
import { Icon, Stack, Text, cn } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

export const PhaseSection: FC<{
  phase: CarePlanViewModel['phase'];
  startDate: string | null;
  disabled?: boolean;
  onSelectPhase: (phase: number) => void;
}> = ({ phase, startDate, disabled, onSelectPhase }) => {
  const { t } = useTranslation();
  return (
    <section className="w-full rounded-xl border border-border-secondary bg-bg-primary p-4 shadow-sm">
      <Stack direction="horizontal" align="baseline" gap={3}>
        <Text weight="semibold">{t('carePlans.phase.title')}</Text>
        {startDate && (
          <Text size="xs" color="secondary">
            {t('carePlans.phase.planStarted', { date: startDate })}
          </Text>
        )}
      </Stack>
      <Stack
        direction="horizontal"
        align="start"
        justify="between"
        gap={2}
        className="mt-4"
      >
        {Array.from({ length: phase.total }, (_, i) => {
          const number = i + 1;
          const isCurrent = number === phase.current;
          const isDone = number < phase.current;
          return (
            <button
              key={number}
              type="button"
              disabled={disabled}
              onClick={() => onSelectPhase(number)}
              className="group flex min-w-0 flex-1 flex-col items-center gap-1.5"
              aria-label={t('carePlans.phase.setPhaseAriaLabel', { number })}
              aria-current={isCurrent ? 'step' : undefined}
            >
              <span
                className={cn(
                  'flex size-8 items-center justify-center rounded-full border text-sm transition-colors',
                  isCurrent &&
                    'border-border-insights-subtle bg-bg-insights-strong-primary text-text-insights-strong',
                  isDone &&
                    'border-border-secondary bg-bg-secondary text-text-secondary',
                  !isCurrent &&
                    !isDone &&
                    'border-border-secondary text-text-secondary group-hover:border-border-insights-subtle',
                )}
              >
                {isDone ? <Icon name="Check" size="xs" /> : number}
              </span>
              <Text
                size="xs"
                color={isCurrent ? 'primary' : 'secondary'}
                className={cn('text-center', isCurrent && 'font-medium')}
              >
                {phase.names[i] ?? t('carePlans.phaseNumber', { number })}
              </Text>
            </button>
          );
        })}
      </Stack>
    </section>
  );
};
